import { useSession } from "next-auth/react";
import useTranslation from "next-translate/useTranslation";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import { Suspense, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { ICON_SIZE, goBack, groupNewResourceLink, isFalsey, isTruthy } from "../../helpers/application";
import { inviteUserToGroup } from "../../helpers/group_helper";
import { defaultToast } from "../../helpers/sweet_alert_helper";
import {
  approveUsersGroup,
  deleteGroup,
  deleteUsersGroup,
  getGroupChatRoom,
  getGroupUsersForInvite,
  postGroupInviteUser,
  usersGroupDisplayedNow,
} from "../../src/queries";
import Navbar from "../navbars/navbar";
import NavbarBottom from "../navbars/navbar_bottom";
import Card from "../shared/card";
import CustomSheet from "../shared/custom_sheet";
import BlockGap from "../shared/gap";
import ImageIcon from "../shared/image_icon";
import LogoImage from "../shared/logo_image";
import AvatarList from "./avatar_list";
import GroupTile from "./group_tile";
import EventsList from "./events_list";
import DummyMargin from "../shared/dummy_margin";
import AboutContent from "./about_content";
import { XLg } from "react-bootstrap-icons";
const InviteSheet = dynamic(() => import("../events/invite_sheet"), { suspense: true });

const Detail = (props) => {
  const { t } = useTranslation("common");
  const [openInviteSheet, setOpenInviteSheet] = useState(false);
  const [openMenuSheet, setOpenMenuSheet] = useState(false);
  const router = useRouter();
  const groupURL = `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}${router.asPath}`;
  const Toast = defaultToast;
  const { data: session } = useSession();
  const membersLink = `/groups/${props.group.token}/members`;

  const currentUserGroup = props.group.current_users_group;

  useEffect(() => {
    if (props.group.current_users_group?.status == "approved" && props.group.undisplayed_events_count > 0) {
      usersGroupDisplayedNow(props.group.current_users_group.id);
    }
  }, [props.group.current_users_group, props.group.undisplayed_events_count]);

  const handleDeleteGroup = () => {
    setOpenMenuSheet(false);

    Swal.fire({
      title: t("groups.confirm_delete"),
      showDenyButton: false,
      showCancelButton: true,
      reverseButtons: false,
      confirmButtonText: t("delete"),
      cancelButtonText: t("cancel"),
      customClass: {
        confirmButton: "btn btn-danger text-white me-2",
        cancelButton: "btn btn-primary",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteGroup(props.group.token)
          .then((response) => {
            Swal.fire({
              text: t("groups.delete_success"),
              icon: "success",
              customClass: {
                confirmButton: "btn btn-primary",
              },
              buttonsStyling: false,
            }).then(() => {
              router.push("/groups");
            });
          })
          .catch((error) => {
            console.log(error);
          });
      }
    });
  };

  const inviteUserRequest = (user) => {
    postGroupInviteUser(props.group.token, user.id)
      .then((response) => {
        props.refetch();
        Toast.fire({
          icon: "success",
          title: t("events.detail.requested"),
        });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const removeUserFromGroup = (users_group) => {
    setOpenMenuSheet(false);

    deleteUsersGroup(users_group.id)
      .then((response) => {
        props.refetch();
        Toast.fire({
          icon: "success",
          title: users_group.status === "approved" ? t("groups.leaved") : t("groups.request_canceled"),
        });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const joinGroup = async (users_group) => {
    const newValue = await approveUsersGroup(users_group.id);
    props.refetch();
  };

  const goToChat = () => {
    getGroupChatRoom(props.group.token)
      .then((data) => {
        router.push(`/chat_rooms/${data.id}`);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleInviteClick = () => {
    setOpenMenuSheet(false);
    setOpenInviteSheet(true);
  };

  const handleAboutClick = () => {
    setOpenMenuSheet(false);
    router.push(`/groups/${props.group.token}/about`);
  };

  return (
    <div className="mb-55 min-100">
      <div
        className="header bg bg-primary cover-image"
        style={{ backgroundImage: props.group.cover_image?.url ? `url(${props.group.cover_image.url})` : "" }}
      >
        <Navbar
          left={
            <li className="nav-item">
              <a className="nav-link float-start ms-0 p-0 cover-image-btn p-1" onClick={() => goBack(router, window)}>
                <XLg size={ICON_SIZE} />
              </a>
            </li>
          }
          fixed={false}
        />
        <div className="d-flex">
          <div className="header-images header-images--center">
            <LogoImage name={props.group.name} src={props.group.logo_thumb} width={80} height={80} />
          </div>
        </div>
      </div>

      <div className="container-sm position-relative pt-46">
        <div className="header-images-content text-center">
          <h2 className="mb-0">{props.group.name}</h2>
        </div>
        <div className="row row-cols-4 align-items-center justify-content-center">
          {currentUserGroup?.status === "approved" && (
            <GroupTile
              text={t("events.title")}
              icon="hands"
              onClick={() => router.push(`/groups/${props.group.token}/events`)}
              showRedDot={props.group.undisplayed_events_count > 0}
            />
          )}

          {currentUserGroup?.status !== "approved" ||
            (currentUserGroup?.status === "approved" && !props.group.community && !props.group.parent && (
              <GroupTile text={t("invite")} icon="plus-accent" onClick={handleInviteClick} />
            ))}

          {currentUserGroup?.status === "approved" && (
            <>
              {props.group.community && (
                <GroupTile
                  text={t("subgroups.title")}
                  icon="groups-blue"
                  onClick={() => router.push(`/groups/${props.group.token}/subgroups`)}
                />
              )}

              {props.group.parent && (
                <GroupTile
                  text={t("community")}
                  icon="groups-blue"
                  onClick={() => router.push(`/groups/${props.group.parent.token}`)}
                />
              )}
              <GroupTile text={t("chats.title")} icon="chat" onClick={goToChat} />
              <GroupTile text={t("others")} icon="dots-blue" onClick={() => setOpenMenuSheet(true)} />
            </>
          )}
        </div>

        <div className="mt-4">
          <div className="row">
            <div className="col text-secondary text-uppercase">
              <small className="float-start label-2 mb-12 text-gray1">{`${t("group")} ${t("members")}`}</small>
            </div>
            <div className="col text-secondary text-uppercase"></div>
          </div>

          <div>
            {props.group.approved_members_count > 0 ? (
              <AvatarList
                users={props.group.members}
                showMoreLink={membersLink}
                totalCount={props.group.approved_members_count}
                membersListLink={`/groups/${props.group.token}/members`}
              />
            ) : (
              <>
                <p>
                  {t("groups.no_members")} {"😞"}
                </p>
                {props.group.can_invite && currentUserGroup?.status === "approved" && (
                  <BlockGap>
                    <button className="btn btn-outline-primary btn-lg mt-3" onClick={() => setOpenInviteSheet(true)}>
                      {t("invite")}
                    </button>
                  </BlockGap>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-4">
          {!currentUserGroup &&
            session?.user &&
            (isFalsey(props.group.parent) || props.group?.parent?.current_users_group?.status == "approved") && (
              <BlockGap>
                <a className="btn btn-primary btn-lg" onClick={() => inviteUserRequest(session.user)}>
                  {t("groups.request_join")}
                </a>
              </BlockGap>
            )}

          {["invited_by_creator", "invited"].includes(currentUserGroup?.status) &&
            session?.user?.id == currentUserGroup?.user?.id && (
              <>
                <BlockGap>
                  <a className="btn btn-primary btn-lg" onClick={() => joinGroup(currentUserGroup)}>
                    {t("approve")}
                  </a>
                </BlockGap>

                <BlockGap>
                  <a
                    className="btn btn-outline-primary btn-lg mt-2 mb-3"
                    onClick={() => removeUserFromGroup(currentUserGroup)}
                  >
                    {t("decline")}
                  </a>
                </BlockGap>
              </>
            )}

          {currentUserGroup &&
            isTruthy(session?.user) &&
            session?.user?.id == currentUserGroup?.user?.id &&
            !["approved", "invited_by_creator"].includes(currentUserGroup.status) &&
            !currentUserGroup.is_admin && (
              <BlockGap>
                <a className="btn btn-outline-primary btn-lg" onClick={() => removeUserFromGroup(currentUserGroup)}>
                  {currentUserGroup.status === "share_allowed"
                    ? t("groups.opt_out")
                    : t("events.detail.cancel_request")}
                </a>
              </BlockGap>
            )}

          {currentUserGroup?.status !== "approved" && (
            <div className="mt-4">
              <AboutContent group={props.group} />
            </div>
          )}

          {!props.current_users_group &&
            props.group.parent &&
            props.group.parent.current_users_group?.status !== "approved" && (
              <div className="mt-4">
                {t("groups.new.community.join_hint")}
                <Card
                  className="justify-content-center mb-0"
                  bodyClassName="row"
                  style={{ backgroundColor: "#e9f6f9" }}
                >
                  <a onClick={() => router.push(`/groups/${props.group.parent.token}`)}>
                    <div className="d-flex align-items-center">
                      <div className="d-flex align-items-center col-auto px-0">
                        <LogoImage
                          name={props.group.parent.name}
                          src={props.group.parent.logo_thumb}
                          width={31}
                          height={31}
                        />
                      </div>
                      <div className="col ms-2">{props.group.parent.name}</div>
                    </div>
                  </a>
                </Card>
              </div>
            )}

          <div className="mt-4">
            <div className="row">
              <div className="col text-secondary text-uppercase">
                {(currentUserGroup?.status === "approved" ||
                  props.group.future_events_count != 0 ||
                  props.group.future_challenges_count != 0) && (
                  <small className="float-start label-2 mb-12 text-gray1">{t("groups.next_events")}</small>
                )}
              </div>
            </div>
            {currentUserGroup?.status === "approved" &&
              props.group.future_events_count == 0 &&
              props.group.future_challenges_count == 0 && (
                <>
                  <p className="font-size-md fw-600">
                    {t("groups.no_events")} {"😞"}
                  </p>

                  <BlockGap>
                    <Link href={`${groupNewResourceLink(props.group.token, "events")}`}>
                      <a className="btn btn-outline-primary btn-lg mt-2 mb-2">{t("events.create_first")}</a>
                    </Link>
                  </BlockGap>
                </>
              )}
            <EventsList token={props.group.token} onlyList={true} />
            <DummyMargin />
          </div>
        </div>
      </div>
      <NavbarBottom />
      <Suspense>
        <InviteSheet
          name={props.group.name}
          token={props.group.token}
          url={groupURL}
          queryCacheKey="usersForInvite"
          getUsersForInvite={getGroupUsersForInvite}
          qParams={props.group.parent ? { users_groups_group_id_eq: props.group.parent.id } : {}}
          inviteUserClick={(user) => {
            inviteUserToGroup(props.group, user, t, () => refetch());
          }}
          isOpen={openInviteSheet}
          onClose={() => setOpenInviteSheet(false)}
          refetchOnInvite={true}
        />
      </Suspense>

      {(props.group.can_update || props.group.can_destroy || currentUserGroup?.status === "approved") && (
        <CustomSheet
          isOpen={openMenuSheet}
          onClose={() => setOpenMenuSheet(false)}
          snapPoints={[0.9, 0]}
          onTapOut={() => setOpenMenuSheet(false)}
        >
          {currentUserGroup?.status === "approved" && (
            <>
              <a onClick={handleAboutClick}>
                <Card className="mb2 text-secondary">
                  <ImageIcon name="about-blue" width={24} height={24} />
                  <span className="ms-2">{t("groups.new.description.label")}</span>
                </Card>
              </a>
              <a onClick={() => router.replace(`/groups/${props.group.token}/polls`)}>
                <Card className="mb2 text-secondary">
                  <ImageIcon name="chart-bar" width={24} height={24} />
                  <span className="ms-2">{t("polls.title")}</span>
                </Card>
              </a>
            </>
          )}

          {currentUserGroup?.status === "approved" && (props.group.community || props.group.parent) && (
            <a onClick={handleInviteClick}>
              <Card className="mb2 text-secondary">
                <ImageIcon name="plus-accent" width={24} height={24} />
                <span className="ms-2">{t("invite")}</span>
              </Card>
            </a>
          )}

          <hr />

          {props.group.can_update && (
            <Link href={`/groups/${props.group.token}/edit`}>
              <a>
                <Card className="mb-2 text-secondary">{t("groups.edit")}</Card>
              </a>
            </Link>
          )}

          {props.group.can_destroy && (
            <a onClick={handleDeleteGroup}>
              <Card className="mb-2 text-secondary">{t("groups.delete")}</Card>
            </a>
          )}

          {currentUserGroup?.status === "approved" && !props.group.can_destroy && (
            <a onClick={() => removeUserFromGroup(currentUserGroup)}>
              <Card className="mb-2 text-secondary">{t("groups.opt_out")}</Card>
            </a>
          )}
        </CustomSheet>
      )}
    </div>
  );
};

export default Detail;
